import type {
  QuotationDetailDto,
  QuotationLineDto,
  QuotationSummaryDto,
} from "@hisense/shared";
import { exportCsv, fileStamp, type CsvColumn } from "./exportCsv";
import { majorUnits } from "@/domain/money";
import { formatDate } from "@/domain/datetime";

export function quotationStatus(
  q: Pick<QuotationSummaryDto, "status" | "validUntil">,
  now = Date.now(),
): "Draft" | "Issued" | "Expired" {
  return q.status === "Issued" && q.validUntil != null && q.validUntil < now
    ? "Expired"
    : q.status;
}
export function quotationLineColumns(
  q: QuotationDetailDto,
  t: (key: string) => string,
): CsvColumn<QuotationLineDto>[] {
  return [
    { header: t("quotations.no"), value: () => q.quotationNo },
    { header: t("quotationWorkspace.revision"), value: () => q.revision },
    { header: t("common.customer"), value: () => q.printCustomer?.companyName },
    { header: t("common.code"), value: (r) => r.productCode },
    { header: t("common.subRef"), value: (r) => r.subRef },
    { header: t("common.description"), value: (r) => r.description },
    { header: t("common.size"), value: (r) => r.size },
    { header: t("common.color"), value: (r) => r.color },
    { header: t("common.unit"), value: (r) => r.unit },
    {
      header: t("common.currencyLabel"),
      value: (r) => r.currency || q.currency,
    },
    { header: t("common.unitPrice"), value: (r) => majorUnits(r.unitPrice, 4) },
    { header: t("common.exwPrice"), value: (r) => majorUnits(r.exwPrice, 4) },
    { header: t("common.dtdPrice"), value: (r) => majorUnits(r.dtdPrice, 4) },
    {
      header: t("common.underMoqPrice"),
      value: (r) => majorUnits(r.underMoqPrice, 4),
    },
    { header: t("common.moq"), value: (r) => r.moq },
    { header: t("common.mcq"), value: (r) => r.mcq },
    { header: t("common.msq"), value: (r) => r.msq },
    { header: t("common.sampleLeadTime"), value: (r) => r.sampleLeadTime },
    { header: t("common.bulkLeadTime"), value: (r) => r.bulkLeadTime },
    { header: t("common.lineRemark"), value: (r) => r.remark },
    {
      header: t("quotationWorkspace.validUntil"),
      value: () => formatDate(q.validUntil),
    },
    {
      header: t("quotationWorkspace.validityTerms"),
      value: () => q.validityTerms,
    },
    { header: t("common.remarks"), value: () => q.remarks },
  ];
}
export function exportQuotationCsv(
  q: QuotationDetailDto,
  t: (key: string) => string,
) {
  exportCsv(
    `quotation-${q.quotationNo}-r${q.revision}-${fileStamp()}`,
    quotationLineColumns(q, t),
    q.items,
  );
}
/**
 * Cột của danh sách báo giá — MỘT bộ, dùng cho cả "trang này" lẫn "toàn bộ đã
 * lọc". Tách ra khỏi hàm tải tệp để nút xuất dùng chung nhận trực tiếp.
 */
export function quotationListColumns(
  t: (key: string) => string,
): CsvColumn<QuotationSummaryDto>[] {
  return [
    { header: t("quotations.no"), value: (r) => r.quotationNo },
    { header: t("quotationWorkspace.revision"), value: (r) => r.revision },
    {
      header: t("common.status"),
      value: (r) =>
        t(
          r.workflowVersion === 0
            ? "quotationWorkspace.legacyReview"
            : `quotationWorkspace.status_${quotationStatus(r)}`,
        ),
    },
    { header: t("common.customer"), value: (r) => r.customerName || r.billToName },
    { header: t("common.date"), value: (r) => formatDate(r.issueDate) },
    { header: t("quotationWorkspace.validUntil"), value: (r) => formatDate(r.validUntil) },
    { header: t("common.currencyLabel"), value: (r) => r.currency },
    { header: t("quotations.preparedBy"), value: (r) => r.preparedBy },
    { header: t("quotations.lines"), value: (r) => r.itemCount },
    { header: t("common.items"), value: (r) => r.itemPreview },
    { header: t("common.remarks"), value: (r) => r.remarks },
  ];
}

export function exportQuotationListCsv(
  rows: QuotationSummaryDto[],
  scope: "page" | "filtered",
  t: (key: string) => string,
) {
  exportCsv(`quotations-${scope}-${fileStamp()}`, quotationListColumns(t), rows);
}
